import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Target, Lightbulb, Users, Zap } from 'lucide-react';

const values = [
  {
    id: 'inovacao',
    icon: Lightbulb,
    title: 'Inovação',
    description: 'Sempre buscando as melhores tecnologias e tendências do mercado.',
  },
  {
    id: 'qualidade',
    icon: Target,
    title: 'Qualidade',
    description: 'Código limpo, performático e seguindo as melhores práticas.',
  },
  {
    id: 'parceria',
    icon: Users,
    title: 'Parceria',
    description: 'Trabalhamos lado a lado com nossos clientes em cada projeto.',
  },
  {
    id: 'agilidade',
    icon: Zap,
    title: 'Agilidade',
    description: 'Entregas rápidas sem comprometer a qualidade do trabalho.',
  },
];

export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const valuesRef = useRef(null);
  const isValuesInView = useInView(valuesRef, { once: true, margin: "-100px" });

  return (
    <section
      id="sobre"
      data-testid="about-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      {/* Background Element */}
      <div className="absolute right-0 top-0 w-1/3 h-full bg-brand-brown/5" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-24 items-center">
          {/* Left Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-6"
            >
              <div className="w-12 h-px bg-brand-light" />
              <span
                data-testid="about-label"
                className="text-xs uppercase tracking-[0.2em] font-bold text-brand-light"
              >
                Sobre Nós
              </span>
            </motion.div>

            <motion.h2
              data-testid="about-title"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-brand-light mb-8"
            >
              Somos a
              <br />
              <span className="text-brand-light">M1LK CODE</span>
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-6"
            >
              <p
                data-testid="about-description-1"
                className="font-body text-lg text-brand-light/70 leading-relaxed"
              >
                Uma agência digital especializada em criar experiências digitais
                que convertem. Nosso foco é entregar soluções sob medida que
                impulsionam o crescimento do seu negócio.
              </p>
              <p
                data-testid="about-description-2"
                className="font-body text-lg text-brand-light/70 leading-relaxed"
              >
                Com expertise em desenvolvimento web e automações, transformamos
                suas ideias em realidade digital, sempre com qualidade,
                performance e design de alto nível.
              </p>
            </motion.div>
          </div>

          {/* Right Content - Values */}
          <div ref={valuesRef}>
            <div
              data-testid="values-grid"
              className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            >
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={value.id}
                    data-testid={`value-card-${value.id}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={isValuesInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.15 }}
                    className="p-6 md:p-8 border border-brand-grey/20 bg-brand-dark/50 backdrop-blur-sm card-hover group"
                  >
                    <div className="relative overflow-hidden w-12 h-12 flex items-center justify-center border border-brand-light/30 text-brand-light mb-6 transition-colors duration-500 before:content-[''] before:absolute before:-left-1 before:-top-1 before:w-[140%] before:h-[140%] before:bg-[radial-gradient(circle_at_0_0,_rgba(46,46,46,1)_0%,_rgba(46,46,46,0.92)_24%,_rgba(46,46,46,0.72)_46%,_rgba(46,46,46,0.35)_68%,_transparent_100%)] before:scale-0 before:origin-top-left before:transition-transform before:duration-700 before:ease-out group-hover:before:scale-[1.8] group-hover:text-brand-dark">
                      <Icon size={22} strokeWidth={1.5} className="relative z-10" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-brand-light mb-3 group-hover:text-brand-light transition-colors">
                      {value.title}
                    </h3>
                    <p className="font-body text-sm text-brand-light/60 leading-relaxed">
                      {value.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
